import { type TypescriptHelpersT } from '@elliemae/ds-typescript-helpers';
import React, { useCallback } from 'react';
import type { ControlledDateTimePickerContextT } from '../../config/useControlledDateTimePicker.js';
import { ControlledDateTimePickerDatatestid } from '../../constants/index.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';
import { StyledHourInput } from '../Styleds.js';
import { convertToPositiveNumberIfPossible } from '../../utils/numberHelpers.js';
const regExpNumbers = /^[0-9]+$/;
const isInvalidHour = (hours: string) => {
  if (hours === '') return false;
  const numberOnly = regExpNumbers.test(hours);
  return hours.length > 2 || hours === '00' || !numberOnly || Number.parseInt(hours, 10) > 12;
};
interface HHInputsPropsT {
  onChange: DSControlledDateTimePickerInternalsT.OnChangeFunc;
  onBlur: DSControlledDateTimePickerInternalsT.OnBlurFunc;
  onFocus: DSControlledDateTimePickerInternalsT.OnFocusFunc;
  onKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown;
  value: string;
  disabled: boolean;
  applyAriaDisabled?: boolean;
  contextRef: React.MutableRefObject<HTMLInputElement | null>;
  placeholder: string;
  autoFocusHourInput: boolean;
  isFocused: boolean;
  getProps: ControlledDateTimePickerContextT['getProps'];
  tabIndex?: TypescriptHelpersT.WCAGTabIndex;
  readOnly: boolean;
}
export const HHInput = React.memo(
  ({
    onChange,
    onBlur,
    onFocus,
    onKeyDown,
    value,
    disabled,
    applyAriaDisabled,
    placeholder,
    contextRef,
    autoFocusHourInput,
    isFocused,
    getProps,
    tabIndex,
    readOnly,
  }: HHInputsPropsT): JSX.Element => {
    const validateOnChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        if (readOnly || applyAriaDisabled || disabled || isInvalidHour(e.target.value)) {
          e.preventDefault();
          return;
        }
        onChange(e);
      },
      [applyAriaDisabled, disabled, onChange, readOnly],
    );
    const hoursNum = convertToPositiveNumberIfPossible(value);
    return (
      <StyledHourInput
        autoFocus={autoFocusHourInput}
        onChange={validateOnChange}
        onBlur={onBlur}
        onFocus={onFocus}
        onKeyDown={onKeyDown}
        value={value}
        innerRef={(DomElem: HTMLInputElement) => {
          contextRef.current = DomElem;
          if (isFocused) DomElem?.focus?.();
        }}
        tabIndex={tabIndex}
        placeholder={placeholder}
        data-testid={ControlledDateTimePickerDatatestid.TIME_INPUTS.HOUR}
        aria-label="hours"
        disabled={disabled}
        aria-disabled={applyAriaDisabled}
        getOwnerProps={getProps}
        readOnly={readOnly}
        role="spinbutton"
        aria-valuemin={1}
        aria-valuemax={12}
        // when empty, valuenow falls back to the min (not omitted) so VoiceOver honors
        // aria-valuetext="empty" instead of announcing a computed position percentage
        aria-valuenow={hoursNum === -1 ? 1 : hoursNum}
        aria-valuetext={hoursNum === -1 ? 'empty' : hoursNum}
      />
    );
  },
);
